import type { ReactElement } from 'react';
import { useSearchParams, type LoaderFunctionArgs } from 'react-router-dom';
import { requireAuth } from '@/services/auth/loaderGuards';
import { ApiError } from '@/api/errors';
import { ProblemSlug } from '@/api/generated-types';

export type ErrorPreviewKind = 'profile-api' | 'profile-render' | 'route-loader';

/** Single definition of the trigger URLs, so a renamed kind fails to compile rather than silently
 * falling through to the `profile-api` default. */
export function devErrorPreviewUrl(kind: ErrorPreviewKind, status?: number): string {
  const params = new URLSearchParams({ preview: kind });
  if (status !== undefined) params.set('status', String(status));
  return `/dev/error-preview?${params}`;
}

function previewKind(params: URLSearchParams): ErrorPreviewKind | null {
  return params.get('preview') as ErrorPreviewKind | null;
}

function simulatedApiError(status: number): ApiError {
  return new ApiError({
    type: status >= 500 ? ProblemSlug.InternalServerError : ProblemSlug.ResourceNotFound,
    title: `Simulated ${status} error`,
    status,
    detail: `This full-page error was triggered from the dev panel to preview the ${status} error page.`,
    traceId: crypto.randomUUID().replace(/-/g, ''),
    helpUrl: `/docs#error-${status >= 500 ? 'internal-server-error' : 'resource-not-found'}`,
  });
}

// Loader errors bypass component boundaries (ProfileErrorBoundary would catch a
// render throw first) — the only way to reach RouteErrorBoundary.
export function devErrorPreviewLoader({ request }: LoaderFunctionArgs): null {
  // Loaders run before ProtectedRoute, so the redirect-to-login must happen here.
  requireAuth(request);
  const url = new URL(request.url);
  if (previewKind(url.searchParams) === 'route-loader')
    throw simulatedApiError(Number(url.searchParams.get('status') ?? 500));
  return null;
}

// Throws on render, so ProtectedRoute's ProfileErrorBoundary catches it.
// Trace ids are fabricated — no backend round-trip.
export default function DevErrorPreview(): ReactElement {
  const [params] = useSearchParams();

  if (previewKind(params) === 'profile-render')
    throw new TypeError(
      "Cannot read properties of undefined (reading 'get') — simulated render crash from the dev panel"
    );

  throw simulatedApiError(Number(params.get('status') ?? 500));
}
